import { useState, useEffect, useCallback } from 'react';
import SecretKeyModal from './SecretKeyModal';

const money = (n) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * Sales-tax-only QuickBooks invoices for orders shipped into nexus states
 * (CA from Apr 2026, NV from Aug 2026). Each invoice charges $0 but carries
 * the taxable basis so QBO computes the destination-rate tax as the balance.
 *
 * Collapsed and empty until the edit key is entered — the dollar figures stay
 * off screen for anyone who just opens the dashboard, and nothing is fetched
 * until the key is verified server-side (POST /api/tax-invoices/unlock).
 */
export default function TaxInvoicesPanel({
  refreshKey,
  secretKey,
  onNeedAuth,
  onAuthFailed,
  onSecretVerified,
}) {
  const [unlocked, setUnlocked] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [showPoModal, setShowPoModal] = useState(false);
  const [poValue, setPoValue] = useState('');
  const [poSavedAt, setPoSavedAt] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [confirm, setConfirm] = useState(null); // null | {limit}
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    fetch('/api/tax-invoices/pending')
      .then((r) => r.json())
      .then((d) => {
        setData(d && !d.error ? d : null);
        setLoadError(d && d.error ? d.error : null);
      })
      .catch(() => setLoadError('Could not load pending tax invoices'));
    fetch('/api/tax-invoices/history')
      .then((r) => r.json())
      .then((d) => setHistory(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch('/api/tax-invoices/quarters')
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d)) {
          setQuarters(d);
          setSelectedQuarter((prev) => prev || d[0]?.quarter || '');
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (unlocked) load();
  }, [load, refreshKey, unlocked]);

  // Verify against EDIT_SECRET before revealing anything.
  const verifyKey = useCallback(
    async (key) => {
      setVerifying(true);
      try {
        const res = await fetch('/api/tax-invoices/unlock', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ secret: key }),
        });
        if (res.status === 403) {
          setUnlockError('Invalid secret key. Please re-enter.');
          setShowUnlockModal(true);
          return;
        }
        if (!res.ok) throw new Error('verify failed');
        setUnlockError(null);
        setShowUnlockModal(false);
        setUnlocked(true);
        // Hand the verified key up so invoice creation doesn't re-prompt
        if (onSecretVerified) onSecretVerified(key);
      } catch {
        setUnlockError("Couldn't verify the key — try again.");
        setShowUnlockModal(true);
      } finally {
        setVerifying(false);
      }
    },
    [onSecretVerified]
  );

  const relock = () => {
    setUnlocked(false);
    setExpanded(false);
    setData(null);
    setHistory([]);
    setQuarters([]);
    setResult(null);
    setLoadError(null);
  };

  const handleUnlockClick = () => {
    if (secretKey) {
      // Already entered this session — check it silently
      verifyKey(secretKey);
      return;
    }
    setUnlockError(null);
    setShowUnlockModal(true);
  };

  if (!unlocked) {
    return (
      <>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6 px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            <h2 className="text-sm font-semibold text-gray-500">Sales Tax Invoices</h2>
            <span className="text-xs text-gray-400">hidden — edit key required</span>
          </div>
          <button
            onClick={handleUnlockClick}
            disabled={verifying}
            className="px-3.5 py-1.5 border border-gray-300 bg-white text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            {verifying ? 'Checking…' : 'Unlock'}
          </button>
        </div>

        {showUnlockModal && (
          <SecretKeyModal
            onSubmit={verifyKey}
            onCancel={() => {
              setShowUnlockModal(false);
              setUnlockError(null);
            }}
            error={unlockError}
            title="Enter edit key"
            description="Required to view sales tax invoices."
            submitLabel="Unlock"
            busy={verifying}
          />
        )}
      </>
    );
  }

  const openPoModal = async () => {
    try {
      const res = await fetch('/api/tax-invoices/po');
      const d = await res.json();
      if (res.ok && d.po) {
        setPoValue(d.po);
        setPoSavedAt(d.savedAt);
      }
    } catch {
      // pre-fill is best-effort
    }
    setShowPoModal(true);
  };

  const handleGeneratePdf = async (includePo = true) => {
    const po = includePo ? poValue.trim() : '';
    if (po) {
      // Remember for next quarter (best-effort — the download proceeds regardless)
      fetch('/api/tax-invoices/po', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po }),
      }).catch(() => {});
    }
    const qs = `quarter=${encodeURIComponent(selectedQuarter)}${po ? `&po=${encodeURIComponent(po)}` : ''}`;
    const a = document.createElement('a');
    a.href = `/api/tax-invoices/consolidated?${qs}`;
    a.download = `RE3-sales-tax-${selectedQuarter}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setShowPoModal(false);
  };

  const handleCreate = async () => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/tax-invoices/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: secretKey,
          ...(confirm?.limit ? { limit: confirm.limit } : {}),
        }),
      });
      const d = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (!res.ok) throw new Error(d.error || 'Failed to create invoices');
      setResult(d);
      setConfirm(null);
      load();
    } catch (err) {
      setError(err.message);
      setConfirm(null);
    } finally {
      setCreating(false);
    }
  };

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6 px-5 py-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-500">Sales Tax Invoices</h2>
          <p className={`text-xs mt-0.5 ${loadError ? 'text-red-600' : 'text-gray-400'}`}>
            {loadError || 'Loading…'}
          </p>
        </div>
        <button onClick={relock} className="text-xs text-gray-400 hover:text-gray-600 font-medium">
          Hide
        </button>
      </div>
    );
  }
  const { totals, orders, configured, missingEnv } = data;
  const nothingPending = totals.pending === 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <h2 className="text-sm font-semibold text-gray-900">Sales Tax Invoices</h2>
          {nothingPending ? (
            <span className="text-xs text-gray-400">all caught up</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-amber-600 px-1.5 py-0.5 rounded-full">
              {totals.valid} to invoice · {money(totals.basisTotal)} basis
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!nothingPending && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              {expanded ? 'Collapse' : `Review ${totals.pending} order${totals.pending !== 1 ? 's' : ''}`}
            </button>
          )}
          <button
            onClick={relock}
            title="Hide this section again — the key will be needed to reopen it"
            className="text-xs text-gray-400 hover:text-gray-600 font-medium"
          >
            Hide
          </button>
        </div>
      </div>

      {configured === false && !nothingPending && (
        <div className="px-5 py-3 text-xs text-amber-800 bg-amber-50 border-b border-amber-100">
          QuickBooks isn't connected yet —{' '}
          {missingEnv.length > 0
            ? <>add env vars <span className="font-mono">{missingEnv.join(', ')}</span> and redeploy.</>
            : <>{data.seedError}</>}
        </div>
      )}

      {error && (
        <div className="px-5 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>
      )}

      {result && (
        <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100">
          <p className="text-xs font-semibold text-emerald-800 mb-1">
            ✓ Created {result.created.length} invoice{result.created.length !== 1 ? 's' : ''}
            {result.failed.length > 0 && ` · ${result.failed.length} failed`}
          </p>
          <div className="text-[11px] text-emerald-800 font-mono space-y-0.5 max-h-32 overflow-y-auto">
            {result.created.map((c) => (
              <div key={c.orderUid}>
                {c.orderUid} ({c.state}) — basis {money(c.basis)}, tax {money(c.tax)} —{' '}
                <a href={c.deepLink} target="_blank" rel="noreferrer" className="underline">
                  {c.docNumber || c.qbInvoiceId}
                </a>
              </div>
            ))}
            {result.failed.map((f) => (
              <div key={f.orderUid} className="text-red-700">{f.orderUid} — {f.error}</div>
            ))}
          </div>
          <button onClick={() => setResult(null)} className="text-[11px] text-emerald-700 underline mt-1">
            dismiss
          </button>
        </div>
      )}

      {expanded && !nothingPending && (
        <>
          <div className="overflow-x-auto max-h-[45vh]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="border-b border-gray-200">
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Order</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">State</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Shipped</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Company</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Cards</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Stands</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Kit</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Basis</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase">Check</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((o) => (
                  <tr key={o.orderUid} className={o.valid ? '' : 'bg-red-50'}>
                    <td className="px-5 py-2 font-mono text-xs font-semibold text-gray-900">{o.orderUid}</td>
                    <td className="px-5 py-2 text-xs text-gray-600">{o.state}</td>
                    <td className="px-5 py-2 text-xs text-gray-500">{o.shipDate}</td>
                    <td className="px-5 py-2 text-xs text-gray-600">{o.company || '—'}</td>
                    <td className="px-5 py-2 text-xs tabular-nums text-gray-600">{o.cards}</td>
                    <td className="px-5 py-2 text-xs tabular-nums text-gray-600">{o.stands}</td>
                    <td className="px-5 py-2 text-xs text-gray-600">{o.kitting ? '$46' : '—'}</td>
                    <td className="px-5 py-2 text-xs tabular-nums font-medium text-gray-900">{money(o.basis)}</td>
                    <td className="px-5 py-2 text-xs">
                      {o.valid ? (
                        <span className="text-emerald-600 font-medium">Ready</span>
                      ) : (
                        o.issues.map((issue, i) => (
                          <span key={i} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                            {issue}
                          </span>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {confirm ? (
            <div className="px-5 py-3.5 border-t border-emerald-200 bg-emerald-50 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-emerald-900">
                <span className="font-semibold">
                  Create {confirm.limit || totals.valid} QuickBooks invoice{(confirm.limit || totals.valid) !== 1 ? 's' : ''} on customer "Capital One"?
                </span>{' '}
                Each is $0 product charge with tax computed on its basis, dated the ship date. This posts to your books.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setConfirm(null)}
                  disabled={creating}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="px-4 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-40"
                >
                  {creating ? 'Creating…' : 'Yes — create in QuickBooks'}
                </button>
              </div>
            </div>
          ) : (
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirm({ limit: 1 })}
                disabled={totals.valid === 0 || configured === false}
                className="px-3.5 py-1.5 border border-gray-300 bg-white text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Create first invoice (test)…
              </button>
              <button
                onClick={() => setConfirm({})}
                disabled={totals.valid === 0 || configured === false}
                className="px-3.5 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Create all {totals.valid}…
              </button>
            </div>
          )}
        </>
      )}

      {quarters.length > 0 && (
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold text-gray-900">Consolidated quarterly PDF</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              One CA roll-up line + NV broken out by month — for submitting to Capital One. Not recorded in QBO.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {quarters.map((q) => (
                <option key={q.quarter} value={q.quarter}>
                  {q.quarter} — {q.invoices} inv · {money(q.tax)}
                </option>
              ))}
            </select>
            <button
              onClick={openPoModal}
              className="px-3.5 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700"
            >
              Download PDF
            </button>
          </div>
        </div>
      )}

      {showPoModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Capital One PO #</h3>
            <p className="text-xs text-gray-400 mb-4">
              Shown in the memo of the {selectedQuarter} consolidated PDF.
              {poSavedAt && (
                <> Pre-filled from your last run ({new Date(poSavedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}) — overwrite if a new PO has started.</>
              )}
            </p>
            <input
              type="text"
              value={poValue}
              onChange={(e) => setPoValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && poValue.trim() && handleGeneratePdf()}
              placeholder="e.g. PO-123456"
              autoFocus
              maxLength={80}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
            />
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => handleGeneratePdf(false)}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                title="Generate the PDF without a PO line"
              >
                Skip PO
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPoModal(false)}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGeneratePdf}
                  disabled={!poValue.trim()}
                  className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40"
                >
                  Generate PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="px-5 py-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">Recent</p>
          <div className="text-[11px] text-gray-600 font-mono space-y-0.5 max-h-28 overflow-y-auto">
            {history.map((h) => (
              <div key={h.order_uid}>
                {h.order_uid} ({h.state}) — {String(h.invoice_date).slice(0, 10)} — basis {money(h.basis_total)},
                tax {money(h.tax_total)} —{' '}
                <a href={h.deepLink} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                  {h.qb_doc_number || h.qb_invoice_id}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
